/**
 * Relations stratégiques — index générique.
 * Liste toute entité annuaire taggée `strategic-relationship` (capacité
 * générique : BOCOM, Adler, family offices... aucun code spécifique dossier).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Handshake, Loader2, ChevronRight, Building2, Users, Briefcase, Radar } from "lucide-react";
import { api } from "@/lib/api";
import { fmtError } from "@/lib/notify";

export default function StrategicRelationships() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/workspace/relationships/strategic")
      .then((r) => setData(r.data))
      .catch((e) => setError(fmtError(e, "Indisponible")));
  }, []);

  if (error) return <div className="text-red-400 text-xs" data-testid="strel-index-error">{error}</div>;
  if (!data) {
    return (
      <div className="text-zinc-500 text-sm py-8 text-center" data-testid="strel-index-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> …
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="strel-index-page">
      <div>
        <h1 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
          <Handshake className="w-5 h-5 text-[#43C6D8]" />
          {t("sr360.title", { defaultValue: "Relations stratégiques" })}
        </h1>
        <p className="text-zinc-400 text-xs max-w-3xl mt-1">
          {t("sr360.intro", { defaultValue: "Une relation stratégique est un système vivant : personnes, sociétés, actifs, financements, deals et signaux. Taggez une entité racine `strategic-relationship` dans l'Annuaire pour la faire apparaître ici." })}
        </p>
      </div>

      {data.items.length === 0 && (
        <div className="text-zinc-500 text-sm border border-zinc-800 rounded-lg p-6 text-center" data-testid="strel-index-empty">
          {t("sr360.empty", { defaultValue: "Aucune relation stratégique déclarée." })}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {data.items.map((it) => (
          <button
            key={it.id}
            data-testid={`strel-card-${it.id}`}
            onClick={() => navigate(`/app/relations/${it.id}`)}
            className="text-left bg-zinc-900/60 border border-zinc-800 hover:border-[#43C6D8]/50 rounded-lg p-4 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="font-medium text-zinc-100">{it.name}</div>
              <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-[#43C6D8]" />
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">{it.sector || it.type} · {it.country || "—"}</div>
            <div className="flex gap-4 mt-3 text-[11px] text-zinc-400">
              <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {it.companies_count} {t("sr360.companies", { defaultValue: "sociétés" })}</span>
              <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {it.persons_count}</span>
              <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {it.deals_count} deals</span>
              {it.watchlisted && <span className="flex items-center gap-1 text-[#EF9F27]"><Radar className="w-3 h-3" /> Radar</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
